import { Link } from 'react-router-dom';

export interface CollectionItem {
  title: string;
  description: string;
  href: string;
  imageSrc: string;
  tag?: string;
}

export interface FeaturedFind {
  title: string;
  tag: string;
  imageSrc: string;
  href: string;
}

export interface PageCollectionsProps {
  heading: string;
  subheading?: string;
  intro?: string;
  items: CollectionItem[];
  cta?: { label: string; href: string };
  featuredFinds?: FeaturedFind[];
}

export default function PageCollections({
  heading,
  subheading,
  intro,
  items,
  cta,
  featuredFinds,
}: PageCollectionsProps) {
  const featured = items[0];
  const rest = items.slice(1);

  return (
    <section className="bg-ivory py-24 lg:py-32">
      <div className="max-w-screen-xl mx-auto px-8 lg:px-12">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-12 gap-6">
          <div>
            {subheading && (
              <div className="flex items-center gap-3 mb-4">
                <span className="w-10 h-px bg-antique" />
                <span className="text-antique text-xs font-sans font-500 tracking-[0.24em] uppercase">{subheading}</span>
              </div>
            )}
            <h2
              className="font-serif text-inkblack"
              style={{ fontSize: 'clamp(2rem, 4.5vw, 3.6rem)', fontWeight: 700, lineHeight: 1.06, letterSpacing: '-0.015em' }}
            >
              {heading}
            </h2>
            {intro && (
              <p className="text-pewter text-sm font-sans font-300 leading-relaxed mt-4 max-w-lg">{intro}</p>
            )}
          </div>
          {cta && (
            <Link
              to={cta.href}
              className="inline-flex items-center gap-3 text-xs font-sans font-600 tracking-[0.16em] uppercase text-inkblack border-b border-inkblack pb-0.5 hover:text-antique hover:border-antique whitespace-nowrap cursor-pointer flex-shrink-0"
            >
              {cta.label}
              <i className="ri-arrow-right-line" />
            </Link>
          )}
        </div>

        {/* New Arrivals Strip — static */}
        {featuredFinds && featuredFinds.length > 0 && (
          <div className="mb-14 p-6 bg-parchment border border-mist">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <span className="w-1.5 h-1.5 rotate-45 border border-antique flex-shrink-0" />
                <span className="text-antique text-xs font-sans font-600 tracking-[0.24em] uppercase">New Arrivals</span>
                <span className="text-pewter text-xs font-sans font-300 tracking-wide">— Fresh finds this week</span>
              </div>
              <span className="text-pewter text-xs font-sans tracking-wide hidden md:block">Inventory rotates weekly</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {featuredFinds.map((find, i) => (
                <Link
                  key={i}
                  to={find.href}
                  className="group cursor-pointer"
                >
                  <div className="aspect-square overflow-hidden relative mb-3">
                    <img
                      src={find.imageSrc}
                      alt={find.title}
                      className="w-full h-full object-cover object-top"
                    />
                    <span className="absolute top-2 left-2 bg-antique text-inkblack text-[9px] font-sans font-700 tracking-[0.14em] uppercase px-2 py-0.5">
                      New
                    </span>
                  </div>
                  <p className="font-serif text-inkblack text-sm font-600 leading-snug mb-0.5 group-hover:text-antique">
                    {find.title}
                  </p>
                  <p className="text-pewter text-xs font-sans tracking-wide">{find.tag}</p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Main Collection Grid — no image scale animations */}
        {featured && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
            {/* Featured large card */}
            <Link
              to={featured.href}
              className="group relative overflow-hidden block cursor-pointer"
              style={{ minHeight: '520px' }}
            >
              <img
                src={featured.imageSrc}
                alt={featured.title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-inkblack/85 via-inkblack/20 to-transparent" />
              {featured.tag && (
                <div className="absolute top-6 left-6">
                  <span className="bg-antique text-inkblack text-xs font-sans font-700 tracking-[0.14em] uppercase px-4 py-1.5">
                    {featured.tag}
                  </span>
                </div>
              )}
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <h3 className="font-serif text-white text-2xl lg:text-3xl font-700 mb-3 leading-tight">{featured.title}</h3>
                <p className="text-white/60 text-sm font-sans font-300 leading-relaxed mb-6 max-w-md">{featured.description}</p>
                <span className="inline-flex items-center gap-3 text-antique text-xs font-sans font-600 tracking-[0.16em] uppercase">
                  Explore Collection <i className="ri-arrow-right-line" />
                </span>
              </div>
            </Link>

            {/* Right column: 2 stacked */}
            <div className="flex flex-col gap-5">
              {rest.slice(0, 2).map((item, i) => (
                <Link
                  key={i}
                  to={item.href}
                  className="group relative overflow-hidden block cursor-pointer flex-1"
                  style={{ minHeight: '248px' }}
                >
                  <img
                    src={item.imageSrc}
                    alt={item.title}
                    className="absolute inset-0 w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-inkblack/80 via-inkblack/10 to-transparent" />
                  {item.tag && (
                    <div className="absolute top-4 left-4">
                      <span className="bg-antique text-inkblack text-[10px] font-sans font-700 tracking-[0.12em] uppercase px-3 py-1">
                        {item.tag}
                      </span>
                    </div>
                  )}
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="font-serif text-white text-xl font-700 mb-1.5 leading-tight">{item.title}</h3>
                    <p className="text-white/55 text-xs font-sans font-300 leading-relaxed mb-4 max-w-xs">{item.description}</p>
                    <span className="inline-flex items-center gap-2 text-antique text-xs font-sans font-600 tracking-[0.14em] uppercase">
                      Explore <i className="ri-arrow-right-line" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Bottom row: remaining items */}
        {rest.length > 2 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {rest.slice(2).map((item, i) => (
              <Link
                key={i}
                to={item.href}
                className="group relative overflow-hidden block cursor-pointer"
                style={{ minHeight: '280px' }}
              >
                <img
                  src={item.imageSrc}
                  alt={item.title}
                  className="absolute inset-0 w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-inkblack/80 via-inkblack/10 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="font-serif text-white text-xl font-700 mb-2 leading-tight">{item.title}</h3>
                  <span className="inline-flex items-center gap-2 text-antique text-xs font-sans font-600 tracking-[0.12em] uppercase">
                    Explore <i className="ri-arrow-right-line" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
